import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../util/errors';
import {
  verifySignupOnServer,
  verifyEmailChangeOnServer,
  fetchProvisioningStatus,
} from '../util/api';
import { OTP_TYPE } from '../util/authConstants';
import { fetchCurrentUser } from './user.duck';
import { authInfo, setEmailJustVerified } from './auth.duck';

const SLICE_NAME = 'emailVerification';

const PROVISIONING_POLL_INTERVAL_MS = 2000;
const PROVISIONING_POLL_TIMEOUT_MS = 30000;

/**
 * Keep checking whether the async half of signup has finished, then refresh the
 * user so the features that depend on it start working.
 *
 * The member is already on their way by the time this runs: signup waits a few
 * seconds for the sync and then answers either way, so this closes the gap
 * without holding anyone on a spinner. A failed check is just a reason to try
 * again, and the whole thing gives up quietly.
 *
 * @param {Function} dispatch
 */
const pollProvisioningInBackground = dispatch => {
  const startedAt = Date.now();

  const check = async () => {
    if (Date.now() - startedAt > PROVISIONING_POLL_TIMEOUT_MS) {
      return;
    }
    try {
      const { provisioned } = await fetchProvisioningStatus();
      if (provisioned) {
        dispatch(fetchCurrentUser({ enforce: true }));
        return;
      }
    } catch (e) {
      // Still unprovisioned as far as this browser knows; try again.
    }
    window.setTimeout(check, PROVISIONING_POLL_INTERVAL_MS);
  };

  window.setTimeout(check, PROVISIONING_POLL_INTERVAL_MS);
};

/**
 * Verification is a single state machine rather than a set of booleans, so the
 * page renders one state per phase and SSR can paint `verifying` for a link
 * that carries a token_hash before the browser has done anything.
 */
export const VERIFICATION_STATUS = {
  IDLE: 'idle',
  VERIFYING: 'verifying',
  VERIFIED: 'verified',
  FAILED: 'failed',
};

/**
 * Verify a Supabase OTP via the BFF. `email_change` refetches the current user
 * (so the new email shows up across the UI). Signup confirmation is
 * browser-aware: if the BFF kept the user logged in (same browser), refresh the
 * auth state and current user so LoginPage forwards them on to onboarding or
 * landing; otherwise set `auth.emailJustVerified` so the LoginPage shows a
 * success banner and asks for a password.
 *
 * @param {{ tokenHash: string, type: string }} args
 */
export const verifyEmail = createAsyncThunk(
  `${SLICE_NAME}/verifyEmail`,
  async ({ tokenHash, type }, { dispatch, rejectWithValue }) => {
    try {
      if (type === OTP_TYPE.EMAIL_CHANGE) {
        await verifyEmailChangeOnServer(tokenHash);
        await dispatch(fetchCurrentUser({ enforce: true }));
        return { type, loggedIn: true };
      }

      const res = await verifySignupOnServer(tokenHash);
      const loggedIn = !!res?.loggedIn;
      if (loggedIn && res?.provisioned === false && typeof window !== 'undefined') {
        pollProvisioningInBackground(dispatch);
      }
      if (loggedIn) {
        // authInfo is what flips auth.isAuthenticated; without it the user is
        // treated as anonymous even though the session cookies are already set.
        await dispatch(authInfo());
        await dispatch(fetchCurrentUser({ enforce: true }));
      } else {
        dispatch(setEmailJustVerified(true));
      }
      return { type, loggedIn };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  },
  {
    // `verifying` is also the state loadData sets before dispatching, so guard
    // on an in-flight request rather than on the status alone.
    condition: (_, { getState }) => !getState().emailVerification.requestInFlight,
  }
);

export const verify = params => dispatch => dispatch(verifyEmail(params));

// ================ Slice ================ //

const emailVerificationSlice = createSlice({
  name: SLICE_NAME,
  initialState: {
    status: VERIFICATION_STATUS.IDLE,
    verificationType: null,
    verificationError: null,
    requestInFlight: false,
    // Whether the BFF kept this browser signed in. Drives whether the verified
    // panel continues into the app or asks for a password.
    loggedIn: false,
  },
  reducers: {
    // Dispatched from loadData on both server and client so the first paint of
    // a link carrying token_hash is the spinner, never a placeholder.
    verificationStarted: (state, action) => {
      state.status = VERIFICATION_STATUS.VERIFYING;
      state.verificationType = action.payload || null;
      state.verificationError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(verifyEmail.pending, state => {
        state.status = VERIFICATION_STATUS.VERIFYING;
        state.verificationError = null;
        state.requestInFlight = true;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.status = VERIFICATION_STATUS.VERIFIED;
        state.verificationType = action.payload?.type || null;
        state.loggedIn = !!action.payload?.loggedIn;
        state.requestInFlight = false;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.status = VERIFICATION_STATUS.FAILED;
        state.verificationError = action.payload;
        state.requestInFlight = false;
      });
  },
});

export const { verificationStarted } = emailVerificationSlice.actions;

export default emailVerificationSlice.reducer;
